import Api from "@ciq/shared/api/api"

/**
 * @typedef {Object} ActiveJob
 * @property {string} job_id - Job ID
 * @property {string} status - Job status (PENDING, SUBMITTED, IN_PROGRESS, COMPLETED, ERROR)
 * @property {string} created_at - Job creation time
 * @property {string|null} submitted_at - Job submission time
 * @property {Object} files - File metadata
 */

/**
 * @typedef {Object} FileInput
 * @property {string} filename - Original filename
 * @property {string} fileType - File type (room_to_zone or space_inventory)
 */

/**
 * @typedef {Object} UploadInfo
 * @property {string} file_type - File type
 * @property {string} filename - Original filename
 * @property {string} upload_url - Pre-signed S3 URL for upload
 * @property {string} s3_key - S3 key where file will be stored
 */

/**
 * @typedef {Object} CreateJobResponse
 * @property {string} job_id - Job ID
 * @property {string} status - Job status (PENDING)
 * @property {UploadInfo[]} uploads - Upload URLs for each file
 */

/**
 * Create a zone upload job and get presigned URLs for all files
 * @param {string} schoolId - School ID
 * @param {FileInput[]} files - Array of files with filename and fileType
 * @returns {Promise<CreateJobResponse>}
 */
export async function createUploadJob(schoolId, files) {
  const response = await Api.post(`/schools/${schoolId}/zone-uploads/jobs`, {
    files: files.map(f => ({
      filename: f.filename,
      file_type: f.fileType
    }))
  })
  return response.data
}

/**
 * Upload file directly to S3 using pre-signed URL
 * @param {string} uploadUrl - Pre-signed S3 URL
 * @param {File} file - File to upload
 * @returns {Promise<void>}
 */
export async function uploadFileToS3(uploadUrl, file) {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type || "application/octet-stream"
    }
  })
  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`)
  }
}

/**
 * Submit job for processing
 * @param {string} schoolId - School ID
 * @param {string} jobId - Job ID
 * @returns {Promise<{job_id: string, status: string}>}
 */
export async function submitJob(schoolId, jobId) {
  const response = await Api.post(`/schools/${schoolId}/zone-uploads/jobs/${jobId}/submit`)
  return response.data
}

/**
 * Get active zone upload job for a school
 * @param {string} schoolId - School ID
 * @returns {Promise<ActiveJob|null>} Active job or null if none exists
 */
export async function getActiveJob(schoolId) {
  const response = await Api.get(`/schools/${schoolId}/zone-uploads/jobs`)
  // 204 No Content means no active job
  if (response.status === 204 || !response.data) {
    return null
  }
  return response.data
}

/**
 * Cancel a zone upload job
 * @param {string} schoolId - School ID
 * @param {string} jobId - Job ID to cancel
 * @returns {Promise<{job_id: string, status: string}>}
 */
export async function cancelJob(schoolId, jobId) {
  const response = await Api.post(`/schools/${schoolId}/zone-uploads/jobs/${jobId}/cancel`)
  return response.data
}

/**
 * @typedef {Object} RoomMappingResult
 * @property {number} total - Total rooms processed
 * @property {number} mapped - Successfully mapped rooms
 * @property {number} unmapped - Rooms without inventory data
 * @property {number} success_rate - Mapping success percentage
 */

/**
 * @typedef {Object} ZonesResult
 * @property {number} total - Total zones with data
 * @property {number} building - Building-level zones
 * @property {number} floor - Floor-level zones
 * @property {number} custom - Custom (default) zones
 */

/**
 * @typedef {Object} CapacityResult
 * @property {number} square_footage - Total square footage
 * @property {number} capacity - Total capacity
 */

/**
 * @typedef {Object} JobResults
 * @property {string} institution - School name
 * @property {string} processing_date - Processing completion date (ISO 8601)
 * @property {RoomMappingResult} room_mapping - Room mapping statistics
 * @property {ZonesResult} zones - Zone breakdown by type
 * @property {CapacityResult} capacity - Capacity and area totals
 * @property {Array<Object>} warnings - Processing warnings
 */

/**
 * @typedef {Object} ErrorDetails
 * @property {string} code - Standardized error code (e.g., MISSING_COLUMNS, States.TaskFailed)
 * @property {string} message - User-friendly error message
 */

/**
 * @typedef {Object} JobStatus
 * @property {string} job_id - Job ID
 * @property {string} status - Job status (PENDING, SUBMITTED, IN_PROGRESS, COMPLETED, ERROR, CANCELLED)
 * @property {string} created_at - Job creation time
 * @property {string|null} submitted_at - Job submission time
 * @property {string|null} completed_at - Job completion time
 * @property {JobResults|null} results - Transformed results (when status is COMPLETED)
 * @property {ErrorDetails|null} error_details - Error details (when status is ERROR)
 */

/**
 * Get job status by ID
 * @param {string} schoolId - School ID
 * @param {string} jobId - Job ID
 * @returns {Promise<JobStatus|null>} Job status or null if not found
 */
export async function getJobById(schoolId, jobId) {
  try {
    const response = await Api.get(`/schools/${schoolId}/zone-uploads/jobs/${jobId}`)
    return response.data
  } catch (error) {
    if (error.response?.status === 404) {
      return null
    }
    throw error
  }
}
